import React, { useEffect, useState, createContext, useContext } from 'react';
import { User, signInWithPopup, signOut as firebaseSignOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, googleProvider, db } from '../services/firebase';
// Define user roles
export type UserRole = 'superadmin' | 'author' | 'editor' | 'writer' | 'contributor' | 'pending' | 'reader' | 'rejected';
// Extended user type with role
export interface ExtendedUser extends User {
  role?: UserRole;
  displayName: string;
  email: string;
}
interface AuthContextType {
  user: ExtendedUser | null;
  loading: boolean;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  hasRole: (roles: UserRole[]) => boolean;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      if (firebaseUser) {
        try {
          console.log('Firebase user authenticated:', firebaseUser.uid);
          // Get user role from Firestore
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          console.log('Checking if user exists in Firestore...');
          const userDoc = await getDoc(userDocRef);
          let role: UserRole = 'pending'; // Default role
          if (userDoc.exists()) {
            console.log('User document exists in Firestore');
            role = userDoc.data().role as UserRole;
          } else {
            console.log('User document does not exist, creating new user...');
            // Create new user in Firestore with pending role
            const userData = {
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
              role: 'pending',
              createdAt: serverTimestamp() // Use serverTimestamp instead of new Date()
            };
            console.log('Attempting to write user data:', userData);
            try {
              await setDoc(userDocRef, userData);
              console.log('Successfully created user document in Firestore');
            } catch (firestoreError: any) {
              console.error('Error creating user document:', firestoreError);
              setError(`Firestore write error: ${firestoreError.message}`);
              // Still set the user but with a note about the error
              setUser({
                ...firebaseUser,
                role: 'pending'
              } as ExtendedUser);
              setLoading(false);
              return;
            }
          }
          // Set user with role
          setUser({
            ...firebaseUser,
            role
          } as ExtendedUser);
        } catch (err: any) {
          console.error('Error in auth state change handler:', err);
          setError(`Error processing authentication: ${err.message}`);
          // Still set the user as authenticated but with no role
          setUser(firebaseUser as ExtendedUser);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);
  const signInWithGoogle = async () => {
    setError(null);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      console.log('Google sign-in successful');
      return result;
    } catch (error: any) {
      console.error('Error signing in with Google:', error);
      // Extract meaningful error message
      let errorMessage = 'Failed to sign in with Google';
      if (error.code) {
        switch (error.code) {
          case 'auth/popup-closed-by-user':
            errorMessage = 'Sign-in popup was closed before completing authentication';
            break;
          case 'auth/unauthorized-domain':
            errorMessage = 'This domain is not authorized for OAuth operations';
            break;
          case 'auth/cancelled-popup-request':
            errorMessage = 'Sign-in popup was cancelled';
            break;
          case 'auth/operation-not-allowed':
            errorMessage = 'Google sign-in is not enabled in Firebase';
            break;
        }
      }
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (error: any) {
      console.error('Error signing out:', error);
      setError(`Error signing out: ${error.message}`);
    }
  };
  const hasRole = (roles: UserRole[]): boolean => {
    if (!user || !user.role) return false;
    return roles.includes(user.role);
  };
  return <AuthContext.Provider value={{
    user,
    loading,
    error,
    signInWithGoogle,
    signOut,
    hasRole
  }} data-id="element-620">
      {children}
    </AuthContext.Provider>;
};
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};